import { GroupingModeEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { SINGLETON_GROUP_LABEL } from '../GroupBanner';
import type { CheckboxListItem } from '../../CheckboxList';

export interface GlobalTotalOptionsGroup {
  readonly _id: Id;
  readonly groupingMode?: GroupingModeEnum;
  readonly isOptional?: boolean;
  readonly label?: string | null;
}

// The groups priced by one same mission (a sub-mission of a collaboration, or
// the collaboration itself), nested under one checkbox.
export interface GlobalTotalOptionsSection {
  readonly _id: Id;
  readonly label: string;
  readonly groupIds: readonly Id[];
}

const groupLabel = (group: GlobalTotalOptionsGroup): string =>
  group.groupingMode === GroupingModeEnum.Singleton
    ? SINGLETON_GROUP_LABEL
    : (group.label ?? '');

const toGroupItem = (group: GlobalTotalOptionsGroup): CheckboxListItem<Id> => ({
  disabled: !group.isOptional,
  id: group._id,
  label: group.isOptional ? `[Option] ${groupLabel(group)}` : groupLabel(group),
});

const toSectionItem = (
  section: GlobalTotalOptionsSection,
  groups: readonly GlobalTotalOptionsGroup[],
): CheckboxListItem<Id> => {
  const children = groups
    .filter((group) => section.groupIds.includes(group._id))
    .map(toGroupItem);

  return {
    children,
    disabled: children.every((child) => child.disabled),
    id: section._id,
    label: section.label,
  };
};

export const buildGlobalTotalOptionItems = (
  groups: readonly GlobalTotalOptionsGroup[],
  sections: readonly GlobalTotalOptionsSection[],
): readonly CheckboxListItem<Id>[] => {
  const sectionGroupIds = sections.flatMap((section) => section.groupIds);

  return [
    ...sections
      .map((section) => toSectionItem(section, groups))
      .filter((item) => !!item.children?.length),
    ...groups
      .filter((group) => !sectionGroupIds.includes(group._id))
      .map(toGroupItem),
  ];
};

// A section only stands for its groups: it is checked when they all are.
export const withCheckedSections = (
  items: readonly CheckboxListItem<Id>[],
  checkedGroupIds: readonly Id[],
): readonly Id[] => [
  ...checkedGroupIds,
  ...items
    .filter(
      (item) =>
        !!item.children?.length &&
        item.children.every((child) => checkedGroupIds.includes(child.id)),
    )
    .map((item) => item.id),
];
